import { Component, ElementRef, computed, effect, inject, input, model, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentPermission, DocumentViewMode, FileTypeCategory } from '../../../../core/enums/data.enums';
import { DocumentsPermission } from '../../../../core/enums/permissions.enums';
import { DocumentListSortOptions } from '../../../../core/enums/sort-options.enums';
import { DocumentViewFilter } from '../../../../core/models/data/document.model';
import { MultiSelectComponent } from '../../../../parts/multi-select/multi-select.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { SortOption } from '../../../../core/models/ui/sort-option.model';
import { ParentFocusDirective } from '../../../../core/directives/parent-focus.directive';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { DropdownActions } from '../../../../core/enums/base.enums';
import { DocumentsConstants } from '../../../../core/constants/documents.constants';
import { DocumentsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { DatePlaceholderDirective } from '../../../../core/directives/date-placeholder.directive';

@Component({
    selector: 'ms-document-toolbar',
    standalone: true,
    imports: [FontAwesomeModule, FormsModule, MultiSelectComponent, SortDropdownComponent, ParentFocusDirective, IfHasPermissionDirective, DatePlaceholderDirective],
    templateUrl: './document-toolbar.component.html',
    styleUrl: './document-toolbar.component.scss'
})
export class DocumentToolbarComponent {
    public filter = model<DocumentViewFilter>({});
    public sort = model<DocumentListSortOptions>(DocumentListSortOptions.NameAsc);
    // Top-level folders selected — eligible for the bulk "Change permissions" action.
    public permissionEligibleCount = input<number>(0);
    // Files selected — eligible for the bulk "Move to…" action.
    public moveEligibleCount = input<number>(0);
    // Folders selected (any level) — eligible for bulk delete.
    public deleteFolderEligibleCount = input<number>(0);
    // Files selected (any status) — eligible for bulk delete.
    public deleteFileEligibleCount = input<number>(0);
    public canUpload = input<boolean>(false);
    // The toolbar only appears while browsing or searching, never in the activity-log view.
    public mode = input<DocumentViewMode.Browse | DocumentViewMode.Search>(DocumentViewMode.Browse);
    public filterLabel = input<string>('Filter');
    // False at the root (folders only) — hides the file-type / modified-date facets.
    public canFilterFiles = input<boolean>(true);
    // False inside a folder (contents inherit one permission) — hides the Access facet.
    public canFilterByAccess = input<boolean>(true);

    public isBrowse = computed<boolean>(() => this.mode() === DocumentViewMode.Browse);

    public newFolderRequested = output<void>();
    public bulkPermissionsRequested = output<void>();
    public bulkMoveRequested = output<void>();
    public bulkDeleteFoldersRequested = output<void>();
    public bulkDeleteFilesRequested = output<void>();
    public uploadFilesRequested = output<File[]>();

    public fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');

    public DocumentPermission = DocumentPermission;
    public FileTypeCategory = FileTypeCategory;
    public DocumentsPermission = DocumentsPermission;
    public DocumentListSortOptions = DocumentListSortOptions;

    public readonly filterDropdownId = DocumentsConstants.FILTER_DROPDOWN_ID();

    public readonly maxFileSizeHint = DocumentsMessagingConstants.DOCUMENTS_UPLOAD_MAX_SIZE_HINT()
        .replaceValuesWithObject({ limit: DocumentsConstants.MAX_FILE_SIZE_LABEL() });

    public permissionOptions: DocumentPermission[] = [
        DocumentPermission.AdminOnly,
        DocumentPermission.MemberRead,
        DocumentPermission.Public
    ];

    public fileTypeOptions: FileTypeCategory[] = [
        FileTypeCategory.Pdf,
        FileTypeCategory.Image,
        FileTypeCategory.Document,
        FileTypeCategory.Spreadsheet,
        FileTypeCategory.Video,
        FileTypeCategory.Archive,
        FileTypeCategory.Other
    ];

    // Size/Type apply to files only, so they're offered only where files can be in scope.
    public sortOptions = computed<SortOption[]>(() => {
        const base: SortOption[] = [
            { label: 'Name', value: 'Name' },
            { label: 'Last modified', value: 'LastModified' }
        ];
        return this.canFilterFiles()
            ? [...base, { label: 'Size', value: 'Size' }, { label: 'Type', value: 'Type' }]
            : base;
    });

    // Drafts seeded from the applied filter; committed to `filter` only on Apply.
    public selectedPermissions = signal<DocumentPermission[]>([]);
    public selectedFileTypes = signal<FileTypeCategory[]>([]);
    public draftModifiedAfter = signal<string | undefined>(undefined);
    public draftModifiedBefore = signal<string | undefined>(undefined);

    public filterCount = computed<number>(() => {
        const f = this.filter();
        return (f.permissions?.length ?? 0)
            + (f.fileTypes?.length ?? 0)
            + (f.modifiedAfter ? 1 : 0)
            + (f.modifiedBefore ? 1 : 0);
    });

    private bootstrapService = inject(BootstrapService);

    constructor() {
        // Re-seed drafts when the applied filter changes.
        effect(() => {
            const f = this.filter();
            this.selectedPermissions.set([...(f.permissions ?? [])]);
            this.selectedFileTypes.set([...(f.fileTypes ?? [])]);
            this.draftModifiedAfter.set(f.modifiedAfter);
            this.draftModifiedBefore.set(f.modifiedBefore);
        });
    }

    public onSortChanged(value: string): void {
        this.sort.set(value as DocumentListSortOptions);
    }

    public onPermissionsChange(values: DocumentPermission[]): void {
        this.selectedPermissions.set(values);
    }

    public onFileTypesChange(values: FileTypeCategory[]): void {
        this.selectedFileTypes.set(values);
    }

    public onApply(): void {
        this.filter.set({
            ...this.filter(),
            permissions: this.selectedPermissions().length > 0 ? this.selectedPermissions() : undefined,
            fileTypes: this.selectedFileTypes().length > 0 ? this.selectedFileTypes() : undefined,
            modifiedAfter: this.draftModifiedAfter() || undefined,
            modifiedBefore: this.draftModifiedBefore() || undefined
        });
        this.closeDropdown();
    }

    public onReset(): void {
        this.selectedPermissions.set([]);
        this.selectedFileTypes.set([]);
        this.draftModifiedAfter.set(undefined);
        this.draftModifiedBefore.set(undefined);
        this.filter.set({
            ...this.filter(),
            permissions: undefined,
            fileTypes: undefined,
            modifiedAfter: undefined,
            modifiedBefore: undefined
        });
    }

    public closeDropdown(): void {
        this.bootstrapService.manageDropdown(`#${this.filterDropdownId}`, DropdownActions.Hide);
    }

    public openFilePicker(): void {
        this.fileInputRef()?.nativeElement.click();
    }

    public onFilesPicked(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;
        this.uploadFilesRequested.emit(Array.from(input.files));
        input.value = '';
    }

    public requestNewFolder(): void {
        this.newFolderRequested.emit();
    }

    public requestBulkPermissions(): void {
        this.bulkPermissionsRequested.emit();
    }

    public requestBulkMove(): void {
        this.bulkMoveRequested.emit();
    }

    public requestBulkDeleteFolders(): void {
        this.bulkDeleteFoldersRequested.emit();
    }

    public requestBulkDeleteFiles(): void {
        this.bulkDeleteFilesRequested.emit();
    }
}
